import { isMobile } from '../utils/device';

interface Particle {
  el: HTMLElement;
  x: number;
  y: number;
  vx: number;
  vy: number;
  rot: number;
  rotSpeed: number;
  life: number;
  decay: number;
}

const NEON_PALETTE = [
  '#22d3ee', '#a855f7', '#7c3aed', '#17e3a2', '#f59e0b',
  '#ec4899', '#6366f1', '#14b8a6', '#f43f5e', '#8b5cf6',
];

const PARTICLE_COUNT = 55;
const PARTICLE_COUNT_MOBILE = 28;
const GRAVITY = 0.28;
const DRAG = 0.975;

export class CrashEffects {
  private container!: HTMLElement;
  private particles: Particle[] = [];
  private animationId: number | null = null;
  private isRunning = false;

  constructor(containerId = 'crash-effects') {
    const el = document.getElementById(containerId) || document.querySelector(`.${containerId}`) as HTMLElement;
    if (!el) return;
    this.container = el;
  }

  private spawn(x: number, y: number): Particle {
    const el = document.createElement('div');
    el.className = 'crash-confetti';

    const size = 5 + Math.random() * 9;
    const isCircle = Math.random() > 0.4;
    const color = NEON_PALETTE[Math.floor(Math.random() * NEON_PALETTE.length)];

    Object.assign(el.style, {
      position: 'absolute',
      width: `${size}px`,
      height: `${size}px`,
      borderRadius: isCircle ? '50%' : '3px',
      backgroundColor: color,
      boxShadow: `0 0 8px ${color}cc, 0 0 16px ${color}55`,
      left: `${x}px`,
      top: `${y}px`,
      pointerEvents: 'none',
      willChange: 'transform, opacity',
      opacity: '1',
    });

    this.container.appendChild(el);

    const angle = Math.random() * Math.PI * 2;
    const speed = 2.5 + Math.random() * 5;

    return {
      el,
      x,
      y,
      vx: Math.cos(angle) * speed,
      vy: Math.sin(angle) * speed - 3,
      rot: Math.random() * 360,
      rotSpeed: (Math.random() - 0.5) * 12,
      life: 1.0,
      decay: 1 / (90 + Math.random() * 30),
    };
  }

  private spawnShockwave(cx: number, cy: number) {
    for (let i = 0; i < 3; i++) {
      const ring = document.createElement('div');
      ring.className = 'crash-expanding-ring';
      ring.style.animationDelay = `${i * 0.1}s`;
      this.container.appendChild(ring);
      setTimeout(() => ring.remove(), 1400);
    }

    const flash = document.createElement('div');
    flash.className = 'crash-explosion-flash';
    this.container.appendChild(flash);
    setTimeout(() => flash.remove(), 700);
  }

  private tick() {
    const bounds = this.container.getBoundingClientRect();

    for (let i = this.particles.length - 1; i >= 0; i--) {
      const p = this.particles[i];

      p.vy += GRAVITY;
      p.vx *= DRAG;
      p.vy *= DRAG;
      p.x += p.vx;
      p.y += p.vy;
      p.rot += p.rotSpeed;
      p.life -= p.decay;

      const alpha = Math.max(0, p.life);
      p.el.style.left = `${p.x}px`;
      p.el.style.top = `${p.y}px`;
      p.el.style.transform = `rotate(${p.rot}deg) scale(${0.5 + alpha * 0.5})`;
      p.el.style.opacity = alpha.toString();

      if (p.life <= 0 || p.y > bounds.height + 60 || p.x < -60 || p.x > bounds.width + 60) {
        p.el.remove();
        this.particles.splice(i, 1);
      }
    }
  }

  private loop = () => {
    if (!this.isRunning || this.particles.length === 0) {
      this.isRunning = false;
      if (this.animationId !== null) { cancelAnimationFrame(this.animationId); this.animationId = null; }
      return;
    }
    this.tick();
    this.animationId = requestAnimationFrame(this.loop);
  };

  triggerCrash(_multiplier?: number) {
    this.clear();

    const rect = this.container.getBoundingClientRect();
    const cx = rect.width / 2;
    const cy = rect.height / 2;

    this.spawnShockwave(cx, cy);

    const count = isMobile() ? PARTICLE_COUNT_MOBILE : PARTICLE_COUNT;
    for (let i = 0; i < count; i++) {
      this.particles.push(this.spawn(cx + (Math.random() - 0.5) * 50, cy + (Math.random() - 0.5) * 50));
    }

    this.isRunning = true;
    this.loop();
  }

  clear() {
    this.isRunning = false;
    for (const p of this.particles) p.el.remove();
    this.particles = [];
    if (this.animationId !== null) { cancelAnimationFrame(this.animationId); this.animationId = null; }
  }

  destroy() { this.clear(); }
}

